import React from "react";
import { AppointmentPhase, Status } from "../../components";
import { NextStepButton } from "../../components/buttons";
import { NoDataAvailableWarning } from "../../components/NoDataAvailableDiv";
import ShowDateTime from "show/components/ShowDateTime";
import KeywordFilter from "components/filters/Filters/KeywordFilter";
import ArtistFilter from "components/filters/Filters/ArtistFilter";
import StatusFilter from "components/filters/Filters/StatusFilter";
import PeriodFilter from "components/filters/Filters/PeriodFilter";
import BookkeepingFilter from "components/filters/Filters/BookkeepingFilter";
import { type FilterValues } from "components/filters/FilterContext";
import { mapArtistFilterToInstruction, mapBookkeepingFilterToInstruction, mapKeywordFilterToInstruction, mapPeriodFilterToInstruction, mapPromoterFilterToInstruction, mapResponseToArtistFilter, mapResponseToBookkeepingFilter, mapResponseToKeywordFilter, mapResponseToPeriodFilter, mapResponseToPromoterFilter, mapResponseToStatusFilter, mapResponseToVenueFilter, mapStatusFilterToInstruction, mapVenueFilterToInstruction } from "components/filters/mappingUtils";
import SelectableList from "components/ListTemplate/SelectableList";
import VenueFilter from "components/filters/Filters/VenueFilter";
import PromoterFilter from "components/filters/Filters/PromoterFilter";
import { useStatuses } from "components/Status/hooks";
import { useClientId } from "common/SessionContext";

interface Props {
  defaultFilter: server.dto.ListShowInternalFilters
  initialInstruction: server.dto.PaginatedShowInternalInstruction
  selectedShows: server.dto.ShowForListInternal[]
  onSelectedShowsChanged: (newShows: server.dto.ShowForListInternal[]) => void
  onNextStep: () => void
  onFilterChanged: (newFilter: server.dto.PaginatedShowInternalInstruction) => void
}

const SelectShowsStepContent = (props: Props) => {
  const clientId = useClientId();
  const statusesQuery = useStatuses("show", clientId);

  const onShowSelect = async (show: server.dto.ShowForListInternal) => {
    const newSelectedShows = [...props.selectedShows, show];
    props.onSelectedShowsChanged(newSelectedShows);
  };

  const onShowRemove = async (show: server.dto.ShowForListInternal) => {
    const newSelectedShows = props.selectedShows.filter(s => s.showId !== show.showId);
    props.onSelectedShowsChanged(newSelectedShows);
  };

  const mapResponseToFilterValues = (
    response: server.dto.ListShowInternalFilters,
    values: FilterValues
  ): Partial<FilterValues> => ({
    keyword: mapResponseToKeywordFilter(response.keyword),
    artist: mapResponseToArtistFilter(response.artists),
    bookkeepings: mapResponseToBookkeepingFilter(response.bookkeepings),
    statuses: mapResponseToStatusFilter(response.statuses, values.statuses),
    venue: mapResponseToVenueFilter(response.venues),
    promoter: mapResponseToPromoterFilter(response.promoters),
    period: mapResponseToPeriodFilter(response.period)
  });

  const mapFilterValuesToInstruction = (
    filter: FilterValues,
    page: number,
    perPage: number
  ): server.dto.PaginatedShowInternalInstruction => {
    return {
      keyword: mapKeywordFilterToInstruction(filter.keyword),
      artistIds: mapArtistFilterToInstruction(filter.artist),
      bookkeepingIds: mapBookkeepingFilterToInstruction(filter.bookkeepings),
      statuses: mapStatusFilterToInstruction(filter.statuses),
      period: mapPeriodFilterToInstruction(filter.period),
      venueIds: mapVenueFilterToInstruction(filter.venue),
      promoterIds: mapPromoterFilterToInstruction(filter.promoter),
      excludeShowIds: props.selectedShows.map(s => s.showId),
      page,
      perPage,
      onlyMySelection: false
    } satisfies server.dto.PaginatedShowInternalInstruction;
  };

  return <>
    <div className="flex grow1" style={{ minHeight: 0 }}>
      <SelectableList
        perPage={20}
        defaultFilter={props.defaultFilter}
        initialInstruction={props.initialInstruction}
        excludeKeys={props.selectedShows.map(s => s.showId)}
        excludeKeysName="excludeShowIds"
        getId={show => show.showId}
        onSelect={onShowSelect}
        layout={{
          type: "card",
          sections: {
            renderTitle: () => null,
            hideTitle: () => true,
            renderBody: show => <div className="flex">
              <div className="margin-right-small"><AppointmentPhase value={show.status.appointmentPhase} displayLabel={false} /></div>
              <div className="grow1">
                <div>{show.title}</div>
                <Status status={show.status} small />
                <div>
                  <ShowDateTime dateTime={show.dateTime} />
                </div>
              </div>
            </div>,
            renderFooter: () => null,
            hideFooter: () => true
          }
        }}
        filters={<>
          <KeywordFilter
            placeholder={RESX.GeneralLabels.Search}
          />
          <ArtistFilter />
          <VenueFilter />
          <PromoterFilter />
          <StatusFilter initialStatuses={statusesQuery.data ?? []} />
          <PeriodFilter />
          <BookkeepingFilter />
        </>}
        fetchFunction={window.CoreApi.Show.PaginatedInternalEnvironment}
        mapFilterValuesToInstruction={mapFilterValuesToInstruction}
        mapResponseToFilterValues={mapResponseToFilterValues}
      />
      <SelectedShows
        selectedShows={props.selectedShows}
        onRemove={onShowRemove}
      />
    </div>
    <div className="flex justify-end">
      <div>
        <NextStepButton
          disabled={props.selectedShows.length === 0}
          onClick={e => {
            e.preventDefault(); props.onNextStep();
          }} />
      </div>
    </div>
  </>;
};

const SelectedShows = ({ selectedShows, onRemove }: {
  selectedShows: server.dto.ShowForListInternal[]
  onRemove: (show: server.dto.ShowForListInternal) => void
}) => (<div className="flex-column" style={{ flex: "0 0 450px" }}>
  <div className="gray-250 padding-large" style={{ textAlign: "center" }}>
    <h2>{site.currentEnvironment.show.plural}</h2>
  </div>
  <div className="padding-large borders grow1 scrollbox-auto">
    {(selectedShows.length === 0) && <div><NoDataAvailableWarning>{RESX.GeneralWarnings.NoItemsHaveBeenSelected}</NoDataAvailableWarning></div>}
    {(selectedShows.length > 0) && <div className="">
      {selectedShows.map(show => (
        <div key={show.showId} className="padding flex gray-100 margin-bottom-small">
          <div className="margin-right-small">
            <AppointmentPhase value={show.status.appointmentPhase} displayLabel={false} />
          </div>
          <div className="grow1 margin-right">
            <div>{show.title}</div>
            <div className="text-sublabel">
              <ShowDateTime dateTime={show.dateTime} />
            </div>
          </div>
          <div>
            <a className="icon-x red" onClick={() => { onRemove(show); }}></a>
          </div>
        </div>
      ))}
    </div>}
  </div>
</div>);

export default SelectShowsStepContent;